import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';

import { MILLISECONDS_PER_SECOND, SECONDS_PER_MINUTE } from 'constants/date';
import { StatusEnum } from 'constants/status';

export const ChecklistEntityType = {
  CHECKLIST: 'Checklist',
  CATEGORY: 'Category',
  ITEM: 'Item',
};

// How deep a checklist detail read goes. Ranked, so a read at one depth also satisfies every
// shallower requirement and a node only triggers the smallest read that can carry its own rule.
export const ChecklistReadDepth = {
  NONE: 0,
  SELF: 1,
  CATEGORIES: 2,
  ITEMS: 3,
};

export const CHECKLIST_READ_PARAMS_BY_DEPTH = {
  [ChecklistReadDepth.SELF]: { includeItems: false, includeCategories: false },
  [ChecklistReadDepth.CATEGORIES]: { includeItems: false, includeCategories: true },
  [ChecklistReadDepth.ITEMS]: { includeItems: true, includeCategories: true },
};

export const CHECKLIST_READ_DEPTH_BY_ENTITY_TYPE = {
  [ChecklistEntityType.CHECKLIST]: ChecklistReadDepth.SELF,
  [ChecklistEntityType.CATEGORY]: ChecklistReadDepth.CATEGORIES,
  [ChecklistEntityType.ITEM]: ChecklistReadDepth.ITEMS,
};

export const ChecklistTargetType = {
  LOAN: 'LOAN',
  COMPANY: 'COMPANY',
};

export const checklistTargetTypeOptions = [
  { label: 'Loan', value: ChecklistTargetType.LOAN },
  { label: 'Company', value: ChecklistTargetType.COMPANY },
];

// The `target.targetType` carried by a checklist-execution-snapshot item (distinct from
// `ChecklistTargetType` above, which is the checklist DEFINITION's target). A PER-ROW-ENTITY
// (Borrower/Reo/Income/Asset/Liability) completeness checklist runs once PER ROW, so its
// snapshots carry that `targetType` and `targetId` = the row's id — used to scope the "Needed"
// completeness highlighting to the specific row it failed for (Borrower: DEV-4684;
// Reo/Income/Asset/Liability financial-info rows: DEV-5801).
export const ChecklistExecutionSnapshotTargetType = {
  LOAN_APPLICATION: 'LoanApplication',
  BORROWER: 'Borrower',
  REO: 'Reo',
  INCOME: 'Income',
  ASSET: 'Asset',
  LIABILITY: 'Liability',
};

// Every per-row entity target type — each drives per-row "Needed" highlighting exactly like
// Borrower (DEV-5801): a snapshot of one of these types is keyed by an `[entityId]`-scoped field
// key so a completeness gap on one row doesn't light every sibling row's form. Kept
// module-private — consumers use the `isEntityScopedChecklistTargetType` predicate below.
const ENTITY_SCOPED_CHECKLIST_TARGET_TYPES = new Set([
  ChecklistExecutionSnapshotTargetType.BORROWER,
  ChecklistExecutionSnapshotTargetType.REO,
  ChecklistExecutionSnapshotTargetType.INCOME,
  ChecklistExecutionSnapshotTargetType.ASSET,
  ChecklistExecutionSnapshotTargetType.LIABILITY,
]);

export const isEntityScopedChecklistTargetType = targetType =>
  ENTITY_SCOPED_CHECKLIST_TARGET_TYPES.has(targetType);

// A POSITIVE set, not "entity-scoped minus Borrower", so a future entity target type has to opt
// in deliberately instead of silently badging every financial-info row (HZ-3605).
const FINANCIAL_RECORD_CHECKLIST_TARGET_TYPES = new Set([
  ChecklistExecutionSnapshotTargetType.REO,
  ChecklistExecutionSnapshotTargetType.INCOME,
  ChecklistExecutionSnapshotTargetType.ASSET,
  ChecklistExecutionSnapshotTargetType.LIABILITY,
]);

export const isFinancialRecordChecklistTargetType = targetType =>
  FINANCIAL_RECORD_CHECKLIST_TARGET_TYPES.has(targetType);

export const ChecklistItemDeterminationType = {
  FALSE: 'False',
  TRUE: 'True',
  VALUE: 'Value',
  MANUAL: 'Manual',
  NOT_APPLICABLE: 'NotApplicable',
  NEEDS_REVIEW: 'NeedsReview',
};

export const checklistItemDeterminationBaseOptions = [
  { name: 'Yes', value: ChecklistItemDeterminationType.TRUE },
  { name: 'No', value: ChecklistItemDeterminationType.FALSE },
];

export const checklistItemDeterminationOptions = [
  ...checklistItemDeterminationBaseOptions,
  { name: 'Value', value: ChecklistItemDeterminationType.VALUE },
  { name: 'Manual', value: ChecklistItemDeterminationType.MANUAL },
  { name: 'Not Applicable', value: ChecklistItemDeterminationType.NOT_APPLICABLE },
  { name: 'Needs Review', value: ChecklistItemDeterminationType.NEEDS_REVIEW },
];

// How a checklist row, category or checklist reads. The boolean `result` stays the gate and the two
// can differ: a parent can pass over an unresolved child, and a parent's stored boolean can lag behind
// a child that was just reviewed.
export const ChecklistResultState = {
  PASSED: 'Passed',
  FAILED: 'Failed',
  NOT_APPLICABLE: 'NotApplicable',
  NEEDS_REVIEW: 'NeedsReview',
  INFORMATIONAL: 'Informational',
};

export const ChecklistItemResultValueType = {
  YES_NO: 'YesNoChecklistItemResultValue',
  VALUE: 'ValueChecklistItemResultValue',
  MANUAL: 'ManualChecklistItemResultValue',
};

// FH-2728: `CheckItemResultType` — the flat-store item's determination-mode discriminator
// (`FlatChecklistItem.ResultType`/`resultType`), distinct from `ChecklistItemResultValueType`
// above (the legacy snapshot payload's polymorphic result-value discriminator). Maps onto
// `ChecklistItemDeterminationType` in `checklistFlatStoreAdapter.js`.
export const CheckItemResultType = {
  YES_OR_NO: 'YesOrNo',
  VALUE: 'Value',
  MANUAL: 'Manual',
};

// FH-1931: the wire-format lane discriminator for a flat-store `ChecklistTarget` REQUEST (the raw
// PascalCase `ChecklistExecutionTargetType` C# enum member name) — as opposed to `ChecklistType`
// below, whose keys are camelCased for `_.camelCase(target.executionType)` comparisons on the
// RESPONSE.
export const ChecklistExecutionTargetType = {
  SAVED: 'Saved',
  PENDING_CHANGES: 'PendingChanges',
};

export const ChecklistResultGenerationType = {
  FOR_APPLICATION: 'ForApplication',
  FOR_CHECKLIST: 'ForChecklist',
};

export const ChecklistSignalREventType = {
  CHECKLIST_RESULTS_CHANGED: 'ChecklistResultsChanged',
  CHECKLIST_EXECUTION_SNAPSHOTS_CHANGED: 'ChecklistExecutionSnapshotsChanged',
};

export const ChecklistDisplaySettingType = {
  FINAL_RESULT: 'FinalResult',
  PROGRESS_RESULT: 'ProgressResult',
};

const PASSED_STATUS = {
  iconType: StatusEnum.SUCCESS,
  textColor: 'success.main',
  labelVariant: LabelVariant.GREEN,
};

const IN_PROGRESS_STATUS = {
  iconType: StatusEnum.WARNING,
  text: 'In Progress',
  textColor: 'warning.main',
  labelVariant: LabelVariant.ORANGE,
};

const NOT_APPLICABLE_STATUS = {
  iconType: StatusEnum.INFO,
  text: 'Not Applicable',
  textColor: 'text.secondary',
  labelVariant: LabelVariant.GRAY,
};

const INFORMATIONAL_STATUS = {
  iconType: StatusEnum.INFO,
  text: 'Informational',
  textColor: 'text.secondary',
  labelVariant: LabelVariant.BLUE,
};

export const CHECKLIST_RESULT_STATE_STATUS_BY_DISPLAY_TYPE = {
  [ChecklistDisplaySettingType.FINAL_RESULT]: {
    [ChecklistResultState.PASSED]: { ...PASSED_STATUS, text: 'Passed' },
    [ChecklistResultState.FAILED]: {
      iconType: StatusEnum.ERROR,
      text: 'Failed',
      textColor: 'error.main',
      labelVariant: LabelVariant.RED,
    },
    [ChecklistResultState.NEEDS_REVIEW]: { ...IN_PROGRESS_STATUS, text: 'Needs Review' },
    [ChecklistResultState.NOT_APPLICABLE]: NOT_APPLICABLE_STATUS,
    [ChecklistResultState.INFORMATIONAL]: INFORMATIONAL_STATUS,
  },
  [ChecklistDisplaySettingType.PROGRESS_RESULT]: {
    [ChecklistResultState.PASSED]: { ...PASSED_STATUS, text: 'Completed' },
    [ChecklistResultState.FAILED]: IN_PROGRESS_STATUS,
    [ChecklistResultState.NEEDS_REVIEW]: IN_PROGRESS_STATUS,
    [ChecklistResultState.NOT_APPLICABLE]: NOT_APPLICABLE_STATUS,
    [ChecklistResultState.INFORMATIONAL]: INFORMATIONAL_STATUS,
  },
};

export const checklistDisplaySettingsOptions = [
  {
    name: 'Failed / Passed',
    value: ChecklistDisplaySettingType.FINAL_RESULT,
  },
  {
    name: 'In Progress / Completed',
    value: ChecklistDisplaySettingType.PROGRESS_RESULT,
  },
];

export const ChecklistType = {
  SAVED: 'saved',
  PENDING_CHANGES: 'pendingChanges',
};

const ChecklistStatusType = {
  COMPLETED: 'Completed',
  INITIAL_IN_PROGRESS: 'InitialInProgress',
  IN_PROGRESS: 'InProgress',
  INITIAL_STALE: 'InitialStale',
  STALE: 'Stale',
};

export const CHECKLIST_RECALCULATING_STATUSES = [
  ChecklistStatusType.INITIAL_IN_PROGRESS,
  ChecklistStatusType.IN_PROGRESS,
];

// A checklist whose first calculation is still running or has failed has no result yet.
export const CHECKLIST_INITIAL_STATUSES = [
  ChecklistStatusType.INITIAL_IN_PROGRESS,
  ChecklistStatusType.INITIAL_STALE,
];

const CHECKLIST_RECALCULATION_SPINNER_TIMEOUT_MINUTES = 15;

// Longer than the server-side watchdog window, so the client only hides a spinner the backend
// itself failed to resolve.
export const CHECKLIST_RECALCULATION_SPINNER_TIMEOUT_MS =
  CHECKLIST_RECALCULATION_SPINNER_TIMEOUT_MINUTES * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

export const ChecklistPendingChangeStatusType = {
  READY_TO_APPLY: 'ReadyToApply',
  NOT_CHANGED: 'NotChanged',
};

const ReportSectionKey = {
  ATR_QM: 'AtrQm',
  HPML: 'Hpml',
  TILA_RESPA: 'TilaRespa',
  HIGH_COST_HOEPA: 'HighCostHoepa',
  STATE_HIGH_COST: 'StateHighCost',
  STATE_PERMISSIBLE_FEE: 'StatePermissibleFee',
  NMLS: 'Nmls',
  DISCLOSURE_TRACKING: 'DisclosureTracking',
  GSE_FANNIE_FREDDIE: 'GseFannieFreddie',
  GSE_FHA: 'GseFha',
  GSE_VA: 'GseVa',
  GSE_USDA: 'GseUsda',
  HELOC_VALIDATION: 'HelocValidation',
};

export const reportSectionKeyOptions = [
  { name: 'ATR/QM', value: ReportSectionKey.ATR_QM },
  { name: 'HPML', value: ReportSectionKey.HPML },
  { name: 'TILA/RESPA', value: ReportSectionKey.TILA_RESPA },
  { name: 'High Cost (HOEPA)', value: ReportSectionKey.HIGH_COST_HOEPA },
  { name: 'State High-Cost', value: ReportSectionKey.STATE_HIGH_COST },
  { name: 'State Permissible Fee', value: ReportSectionKey.STATE_PERMISSIBLE_FEE },
  { name: 'NMLS', value: ReportSectionKey.NMLS },
  { name: 'Disclosure Tracking', value: ReportSectionKey.DISCLOSURE_TRACKING },
  { name: 'GSE Fannie/Freddie', value: ReportSectionKey.GSE_FANNIE_FREDDIE },
  { name: 'GSE FHA', value: ReportSectionKey.GSE_FHA },
  { name: 'GSE VA', value: ReportSectionKey.GSE_VA },
  { name: 'GSE USDA', value: ReportSectionKey.GSE_USDA },
  { name: 'HELOC Validation', value: ReportSectionKey.HELOC_VALIDATION },
];

export const reportSectionKeyOptionsMap = Object.fromEntries(
  reportSectionKeyOptions.map(option => [option.value, option]),
);

const ReportFieldKey = {
  HPML_APR: 'HpmlApr',
  HPML_APOR: 'HpmlApor',
  HPML_RATE_SPREAD: 'HpmlRateSpread',
  HPML_THRESHOLD: 'HpmlThreshold',
  QM_TOTAL_LOAN_AMOUNT: 'QmTotalLoanAmount',
  QM_POINTS_FEES_LIMIT_PCT: 'QmPointsFeesLimitPct',
  QM_POINTS_FEES_AMOUNT: 'QmPointsFeesAmount',
  QM_MAX_ALLOWABLE: 'QmMaxAllowable',
  QM_AMOUNT_FINANCED: 'QmAmountFinanced',
  HOEPA_POINTS_FEES_THRESHOLD: 'HoepaPointsFeesThreshold',
  HOEPA_TOTAL_LOAN_AMOUNT: 'HoepaTotalLoanAmount',
  HOEPA_TOTAL_POINTS_FEES: 'HoepaTotalPointsFees',
  TRID_APR_DISCLOSED: 'TridAprDisclosed',
  TRID_APR_CALCULATED: 'TridAprCalculated',
  TRID_FINANCE_CHARGE: 'TridFinanceCharge',
  TRID_TOTAL_OF_PAYMENTS: 'TridTotalOfPayments',
};

export const reportFieldKeyOptions = [
  { name: 'APR', value: ReportFieldKey.HPML_APR, group: 'HPML' },
  { name: 'APOR', value: ReportFieldKey.HPML_APOR, group: 'HPML' },
  { name: 'Rate Spread', value: ReportFieldKey.HPML_RATE_SPREAD, group: 'HPML' },
  { name: 'Threshold', value: ReportFieldKey.HPML_THRESHOLD, group: 'HPML' },
  { name: 'Total Loan Amount', value: ReportFieldKey.QM_TOTAL_LOAN_AMOUNT, group: 'ATR/QM' },
  {
    name: 'Points & Fees Limit %',
    value: ReportFieldKey.QM_POINTS_FEES_LIMIT_PCT,
    group: 'ATR/QM',
  },
  { name: 'Points & Fees Amount', value: ReportFieldKey.QM_POINTS_FEES_AMOUNT, group: 'ATR/QM' },
  { name: 'Max Allowable', value: ReportFieldKey.QM_MAX_ALLOWABLE, group: 'ATR/QM' },
  { name: 'Amount Financed', value: ReportFieldKey.QM_AMOUNT_FINANCED, group: 'ATR/QM' },
  {
    name: 'Points & Fees Threshold',
    value: ReportFieldKey.HOEPA_POINTS_FEES_THRESHOLD,
    group: 'HOEPA',
  },
  { name: 'Total Loan Amount', value: ReportFieldKey.HOEPA_TOTAL_LOAN_AMOUNT, group: 'HOEPA' },
  { name: 'Total Points & Fees', value: ReportFieldKey.HOEPA_TOTAL_POINTS_FEES, group: 'HOEPA' },
  { name: 'APR Disclosed', value: ReportFieldKey.TRID_APR_DISCLOSED, group: 'TRID' },
  { name: 'APR Calculated', value: ReportFieldKey.TRID_APR_CALCULATED, group: 'TRID' },
  { name: 'Finance Charge', value: ReportFieldKey.TRID_FINANCE_CHARGE, group: 'TRID' },
  { name: 'Total of Payments', value: ReportFieldKey.TRID_TOTAL_OF_PAYMENTS, group: 'TRID' },
];

export const reportFieldKeyOptionsMap = Object.fromEntries(
  reportFieldKeyOptions.map(option => [option.value, option]),
);

const ChecklistItemSeverity = {
  ERROR: 'Error',
  WARNING: 'Warning',
  INFO: 'Info',
};

export const checklistItemSeverityOptions = [
  { name: 'Error', value: ChecklistItemSeverity.ERROR },
  { name: 'Warning', value: ChecklistItemSeverity.WARNING },
  { name: 'Info', value: ChecklistItemSeverity.INFO },
];

export const checklistItemSeverityOptionsMap = Object.fromEntries(
  checklistItemSeverityOptions.map(option => [option.value, option]),
);

export const ChecklistItemValueFormat = {
  CURRENCY: 'Currency',
  PERCENTAGE: 'Percentage',
  DATE: 'Date',
  NUMBER: 'Number',
  TEXT: 'Text',
};

export const checklistItemValueFormatOptions = [
  { name: 'Currency', value: ChecklistItemValueFormat.CURRENCY },
  { name: 'Percentage', value: ChecklistItemValueFormat.PERCENTAGE },
  { name: 'Date', value: ChecklistItemValueFormat.DATE },
  { name: 'Number', value: ChecklistItemValueFormat.NUMBER },
  { name: 'Text', value: ChecklistItemValueFormat.TEXT },
];

export const checklistItemValueFormatOptionsMap = Object.fromEntries(
  checklistItemValueFormatOptions.map(option => [option.value, option]),
);
